import type { HeatCircuitTileWidget } from '../../../widgets';
import {
  BindingRow,
  CheckboxField,
  NumberField,
  OptionalBindingRow,
  Row,
  Section,
  TextField,
  patcher,
  rangeError,
  stepError,
  type WidgetEditorProps,
} from '../fields';
import { AiHintSection } from './AiHintSection';

export function HeatCircuitTileEditor(
  { widget, plantId, missing, onChange }: WidgetEditorProps<HeatCircuitTileWidget>,
) {
  const patch = patcher(widget, onChange);
  const range = rangeError(widget.setpointMin, widget.setpointMax);
  return (
    <>
      <Section title="Heizkreis">
        <TextField
          label="Name des Heizkreises"
          value={widget.circuitName}
          keepEmptyString
          required
          missing={missing.has('circuitName')}
          placeholder="Erdgeschoss (Fußboden)"
          hint="Der Name des Kreises, nicht die Kachel-Überschrift. Steht auch in der Detailansicht."
          onChange={(next) => patch({ circuitName: next ?? '' })}
        />
      </Section>

      <Section title="Temperaturen">
        <BindingRow
          label="Vorlauf"
          value={widget.flowTemperaturePoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          onChange={(next) => patch({ flowTemperaturePoint: next })}
        />
        <OptionalBindingRow
          label="Rücklauf"
          value={widget.returnTemperaturePoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          onChange={(next) => patch({ returnTemperaturePoint: next })}
        />
        <OptionalBindingRow
          label="Raumtemperatur"
          value={widget.roomTemperaturePoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          hint="Die Zahl, die erklärt, warum der Kreis läuft."
          onChange={(next) => patch({ roomTemperaturePoint: next })}
        />
        <OptionalBindingRow
          label="Außentemperatur"
          value={widget.outsideTemperaturePoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          hint="Der Bezug der Heizkurve."
          onChange={(next) => patch({ outsideTemperaturePoint: next })}
        />
      </Section>

      <Section title="Sollwert">
        <BindingRow
          label="Sollwert"
          value={widget.setpointPoint}
          plantId={plantId}
          filter={{ valueType: 'number', writable: widget.setpointWritable !== false }}
          onChange={(next) => patch({ setpointPoint: next })}
        />
        <CheckboxField
          label="Sollwert verstellbar"
          checked={widget.setpointWritable !== false}
          hint="Aus heißt: nur anzeigen. Dann darf der Punkt auch ein Lesepunkt sein."
          onChange={(next) => patch({ setpointWritable: next })}
        />
        <Row>
          <NumberField
            label="Unten"
            value={widget.setpointMin}
            error={range}
            onChange={(next) => patch({ setpointMin: next })}
          />
          <NumberField
            label="Oben"
            value={widget.setpointMax}
            error={range ? ' ' : undefined}
            onChange={(next) => patch({ setpointMax: next })}
          />
          <NumberField
            label="Schrittweite"
            value={widget.setpointStep}
            min={0}
            error={stepError(widget.setpointStep)}
            onChange={(next) => patch({ setpointStep: next })}
          />
        </Row>
      </Section>

      <Section title="Stellglieder">
        <OptionalBindingRow
          label="Betriebsart"
          value={widget.modePoint}
          plantId={plantId}
          filter={{ valueType: 'enum' }}
          hint="Komfort, Absenken, Aus, Sommer. Die Beschriftungen kommen aus dem Punkt."
          onChange={(next) => patch({ modePoint: next })}
        />
        <OptionalBindingRow
          label="Ventilstellung"
          value={widget.valvePositionPoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          onChange={(next) => patch({ valvePositionPoint: next })}
        />
        <OptionalBindingRow
          label="Pumpe"
          value={widget.pumpStatePoint}
          plantId={plantId}
          onChange={(next) => patch({ pumpStatePoint: next })}
        />
      </Section>

      <AiHintSection value={widget.ai} onChange={(next) => patch({ ai: next })} />
    </>
  );
}
